/**
 * Score explanation — E06-F05-T07 ([BUG] «процент скоринга непонятен пользователю»).
 *
 * The Research screens print a bare percentage next to a company ("5%", "0%", "8%").
 * Two things were invisible to the user and are made explicit here:
 *
 *  1. WHAT the percentage is made of. It is NOT a similarity score: it is
 *     `Σ(weight × match) / Σ(weight) × 100` over the six weighted criteria, where a
 *     criterion the AI check could not CITE verbatim on the company's own site is
 *     omitted from `breakdown` and therefore counts as 0 (see
 *     `researchAdapterResolve.evidenceToBreakdown` + `computePctMatch`, whose
 *     `breakdown[key] ?? 'fail'` fallback resolves an omitted key to fail). That is
 *     exactly why real runs show 5-10%: most criteria came back `unclear`
 *     ("not proven"), not `fail` ("checked and does not match").
 *
 *  2. WHY a company with a very low percentage is on the list at all. The long-list
 *     is built by the FREE discovery stage (`discovered` → `deduped`) from the
 *     recruiter's criteria and is deliberately NOT pre-filtered by score — the paid
 *     AI check runs later, per company, on an explicit click (E06-F05-T04/T05). So
 *     the percentage is the RESULT of the review step, never its entry ticket.
 *
 * This module is PURE (no zod-runtime dependency beyond the schemas, no IO): it turns
 * data every consumer already holds (`breakdown`, `evidence`, `score`, `grade`,
 * `stage`, `aiStatus`) into a render-ready explanation, so the AI-check screen and the
 * Review table show the SAME numbers and the same wording.
 *
 * Weights: `DEFAULT_WEIGHTS` by default — the live scorer
 * (`researchAiChainWorker` → `scoreCandidate`) is invoked WITHOUT a `weights`
 * argument, so the default profile is what actually produced the stored score. A
 * caller that scores with an explicit profile must pass the same profile here, or the
 * breakdown would not add up to the number on screen.
 *
 * Feature-owned; re-exported from `@eer/shared` (packages/shared/src/index.ts).
 */
import { z } from 'zod';
import { SCORE_CRITERIA, DEFAULT_WEIGHTS, VERDICT_MATCH, } from './research-pipeline.js';
// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
/** Human labels for the six weighted criteria (same wording as the Review legend). */
export const SCORE_CRITERION_LABELS = {
    industry: 'Industry',
    subIndustry: 'Sub-industry',
    geo: 'Geography',
    size: 'Company size',
    founded: 'Founded window',
    services: 'Services match',
};
/**
 * Default grade cuts of the scoring stage (`researchPipelineStages.assignGrade`).
 * Shown in the explanation so "why is there no grade" is answerable on screen.
 */
export const SCORE_EXPLAIN_GRADE_BANDS = { a: 80, b: 60, c: 40 };
/**
 * Per-factor verdict as the USER sees it. `unclear` is the important one: the AI
 * check found no citable sentence for this criterion, so it scored 0 — that is
 * "not proven", NOT "does not match".
 */
export const SCORE_FACTOR_VERDICTS = ['pass', 'partial', 'fail', 'unclear'];
export const scoreFactorVerdictSchema = z.enum(SCORE_FACTOR_VERDICTS);
/** How the row got onto the long-list (answers "why is this low score here?"). */
export const SCORE_INCLUSION_SOURCES = ['discovery', 'manual'];
export const scoreInclusionSourceSchema = z.enum(SCORE_INCLUSION_SOURCES);
// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
/** ONE line of the breakdown: what this criterion was worth and what it earned. */
export const scoreFactorSchema = z.object({
    key: z.enum(SCORE_CRITERIA),
    label: z.string(),
    /** Weight of the criterion in the active profile (points out of 100). */
    weight: z.number(),
    verdict: scoreFactorVerdictSchema,
    /** Points this criterion actually contributed (weight × pass 1 / partial .5 / 0). */
    points: z.number(),
    /** True when the verify step stored a verbatim quote + source url for it. */
    cited: z.boolean(),
    /** One-line plain-language reading of this factor. */
    note: z.string(),
});
/** The full render-ready explanation behind one percentage. */
export const scoreExplanationSchema = z.object({
    /** False until the paid AI check produced a score (the cell then shows "—"). */
    scored: z.boolean(),
    score: z.number().nullable(),
    grade: z.enum(['A', 'B', 'C']).nullable(),
    /** Σ points earned and the total the profile can award (normally 100). */
    earnedPoints: z.number(),
    maxPoints: z.number(),
    factors: z.array(scoreFactorSchema),
    /** How many criteria are confirmed / partial / contradicted / unproven. */
    confirmedCount: z.number(),
    partialCount: z.number(),
    failedCount: z.number(),
    unprovenCount: z.number(),
    bands: z.object({ a: z.number(), b: z.number(), c: z.number() }),
    /** One-sentence reading of the number itself. */
    headline: z.string(),
    /** Where the row came from + the stage it joined the long-list at. */
    inclusion: z.object({
        source: scoreInclusionSourceSchema,
        /** The pipeline stage at which it entered the long-list ('discovered'/'manual'). */
        stage: z.string(),
        label: z.string(),
    }),
    /** Why a company with this score is (still) on the list for review. */
    listReason: z.string(),
    /** Extra note when the score is low BECAUSE evidence was missing (nullable). */
    unprovenNote: z.string().nullable(),
    /** Why there is no grade (below the cut / scope-clipped / not scored). */
    gradeNote: z.string().nullable(),
    /** The stored one-line model rationale, when there is one. */
    rationale: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------
/**
 * Build the explanation behind ONE percentage. Deterministic and side-effect free:
 * the same row always yields the same text, so the tooltip in the AI-check screen and
 * the one in the Review table can never disagree.
 */
export function buildScoreExplanation(input) {
    const weights = input.weights ?? DEFAULT_WEIGHTS;
    const breakdown = asRecord(input.breakdown);
    const evidence = asRecord(input.evidence);
    const bands = {
        a: input.bands?.a ?? SCORE_EXPLAIN_GRADE_BANDS.a,
        b: input.bands?.b ?? SCORE_EXPLAIN_GRADE_BANDS.b,
        c: input.bands?.c ?? SCORE_EXPLAIN_GRADE_BANDS.c,
    };
    const factors = SCORE_CRITERIA.map((key) => {
        const weight = weights[key] ?? DEFAULT_WEIGHTS[key] ?? 0;
        const stored = readVerdict(breakdown[key]);
        const entry = asRecord(evidence[key]);
        const evidenceVerdict = readFactorVerdict(entry.verdict);
        const quote = typeof entry.quote === 'string' ? entry.quote.trim() : '';
        const url = typeof entry.url === 'string' ? entry.url.trim() : '';
        const cited = quote.length > 0 && url.length > 0;
        // `breakdown` is the scorer's input and wins; a criterion missing from it earned
        // nothing (computePctMatch reads it as 'fail'), and the evidence map tells us
        // WHY — 'unclear' (nothing citable) reads very differently from a real 'fail'.
        const verdict = stored ?? evidenceVerdict ?? 'unclear';
        const points = round2(weight * (stored ? VERDICT_MATCH[stored] : 0));
        return {
            key,
            label: SCORE_CRITERION_LABELS[key],
            weight,
            verdict,
            points,
            cited,
            note: factorNote(verdict, weight, points),
        };
    });
    const maxPoints = round2(factors.reduce((acc, f) => acc + f.weight, 0));
    const earnedPoints = round2(factors.reduce((acc, f) => acc + f.points, 0));
    const confirmedCount = factors.filter((f) => f.verdict === 'pass').length;
    const partialCount = factors.filter((f) => f.verdict === 'partial').length;
    const failedCount = factors.filter((f) => f.verdict === 'fail').length;
    const unprovenCount = factors.filter((f) => f.verdict === 'unclear').length;
    const scored = input.score != null;
    const inclusion = buildInclusion(input);
    const rationale = (input.rationale ?? '').trim() || null;
    return {
        scored,
        score: input.score,
        grade: input.grade,
        earnedPoints,
        maxPoints,
        factors,
        confirmedCount,
        partialCount,
        failedCount,
        unprovenCount,
        bands,
        headline: buildHeadline({ scored, score: input.score, earnedPoints, maxPoints }),
        inclusion,
        listReason: buildListReason({ scored, score: input.score, bands, inclusion }),
        unprovenNote: buildUnprovenNote(unprovenCount, factors.length, scored),
        gradeNote: buildGradeNote({ scored, score: input.score, grade: input.grade, bands }),
        rationale,
    };
}
// ---------------------------------------------------------------------------
// Copy (English — the app UI language)
// ---------------------------------------------------------------------------
function factorNote(verdict, weight, points) {
    switch (verdict) {
        case 'pass':
            return `Confirmed by a quote from the company's own site — full ${weight} of ${weight} points.`;
        case 'partial':
            return `Partly matches — counted at half weight, ${points} of ${weight} points.`;
        case 'fail':
            return `Checked and does not match the request — 0 of ${weight} points.`;
        default:
            return `Not proven: the check found nothing citable about it, so it scores 0 of ${weight} points. It is not a mismatch — it is unverified.`;
    }
}
function buildHeadline(input) {
    if (!input.scored) {
        return 'Not scored yet — the percentage appears after the AI check confirms criteria on the company site.';
    }
    return `${input.score}% = ${input.earnedPoints} of ${input.maxPoints} weighted points confirmed on the company's own website.`;
}
function buildInclusion(input) {
    if (input.isManual) {
        return {
            source: 'manual',
            stage: 'manual',
            label: 'Added to the long-list by hand by a specialist — it was never filtered by score.',
        };
    }
    return {
        source: 'discovery',
        stage: 'discovered',
        label: 'Added at the discovery stage (free search by the request criteria), before anything was scored — the long-list is never pre-filtered by percentage.',
    };
}
function buildListReason(input) {
    const entry = input.inclusion.label;
    if (!input.scored) {
        return `${entry} Nothing has been verified for it yet, so it has no percentage — run the AI check to get one.`;
    }
    if ((input.score ?? 0) < input.bands.c) {
        return `${entry} The percentage is the RESULT of the check, not the reason it is here: it only counts criteria the check could quote verbatim from the site. A low number usually means "not proven from the website", so review the evidence before rejecting.`;
    }
    return `${entry} The percentage counts only what the check could quote verbatim from the company site.`;
}
function buildUnprovenNote(unprovenCount, total, scored) {
    if (!scored || unprovenCount === 0)
        return null;
    return `${unprovenCount} of ${total} criteria had no citable evidence on the site and were counted as 0. Open Sources / Evidence to see what the check actually read.`;
}
function buildGradeNote(input) {
    if (!input.scored)
        return 'No grade yet — this company has not been AI-checked.';
    if (input.grade != null) {
        const cut = input.grade === 'A' ? input.bands.a : input.grade === 'B' ? input.bands.b : input.bands.c;
        return `Grade ${input.grade}: the score is at or above the ${cut}% cut for this request.`;
    }
    return `No grade: ${input.score}% is below the ${input.bands.c}% Grade-C cut (or the request only asks for higher grades). The company stays on the list so a human can confirm or reject it.`;
}
// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function asRecord(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {};
}
/** Read a scorer-side verdict (pass|partial|fail); anything else → null. */
function readVerdict(value) {
    return value === 'pass' || value === 'partial' || value === 'fail' ? value : null;
}
/** Read a display verdict off the evidence map (adds 'unclear'). */
function readFactorVerdict(value) {
    return SCORE_FACTOR_VERDICTS.includes(value)
        ? value
        : null;
}
function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
//# sourceMappingURL=research-score-explain.js.map